'use client';

import Link from 'next/link';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
} from '@simulmedia/ui-kit';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import Author from './author';
import { cn } from '@skybeam/shared-utils/classnames/cn';

type ResourceCardProps = {
  alt?: string;
  author?: string | null;
  cardDescriptionStyles?: string;
  cardTitleStyles?: string;
  category?: string | null;
  className?: string;
  imageUrl?: string | null;
  onDarkBackground?: boolean;
  preview?: string | null;
  publishedAt?: string | null;
  slug: string;
  timeToRead?: number | null;
  title?: string | null;
};

export default function ResourceCard({
  alt,
  author,
  category,
  className,
  imageUrl,
  onDarkBackground,
  preview,
  publishedAt,
  slug,
  timeToRead,
  title,
}: ResourceCardProps) {
  const router = useRouter();
  return (
    <Card
      data-test="latest-resources-card"
      className={cn(
        'tablet:w-full laptop:w-[282px] desktop:w-[367px] flex max-w-3xl cursor-pointer flex-col rounded-none border-none bg-white shadow-none',
        className,
      )}
      onClick={() => router.push(`/blog/${slug}`)}
    >
      <CardHeader className="p-0">
        <div
          data-test="latest-resources-card-image"
          className="desktop:h-60 laptop:h-40 tablet:h-35.5 relative h-40.5 w-full pb-5"
        >
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={alt ?? 'Image for Resource'}
              className="rounded object-cover"
              fill
            />
          ) : (
            <p className="text-center">No Image Available</p>
          )}
        </div>
        <Author
          author={author}
          publishedAt={publishedAt}
          timeToRead={timeToRead}
          onDarkBackground={onDarkBackground}
        />
      </CardHeader>

      <CardContent className="p-0">
        <CardTitle
          data-test="latest-resources-card-title"
          className={cn(
            'font-light',
            onDarkBackground
              ? 'text-[2rem] leading-[1.1] text-white'
              : 'heading-marketing-4 text-[27px] leading-[1.1] text-black',
          )}
        >
          <Link data-test="latest-resources-card-link" href={`/blog/${slug}`}>
            {title}
          </Link>
        </CardTitle>
        <CardDescription
          data-test="latest-resources-card-preview"
          className={cn(
            'pt-2 text-base leading-normal',
            onDarkBackground ? 'text-white' : 'text-[#737C8B]',
          )}
        >
          {preview}
        </CardDescription>

        {category && onDarkBackground && (
          <div className="mt-6 flex flex-row flex-wrap gap-2">
            <Badge
              data-test="blog-article-category"
              variant="outline"
              className={cn(
                'w-fit rounded-md border px-2.5 py-1.5 text-[0.9375rem] leading-normal font-medium',
                onDarkBackground
                  ? 'border-white text-white'
                  : 'border-[#CDD5DF] text-[#737C8B]',
              )}
            >
              {category}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
